import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ResultsSectionNav from '../../components/ui/ResultsSectionNav';
import CalculationStatus from '../../components/ui/CalculationStatus';
import PlanetaryPositionsSection from './components/PlanetaryPositionsSection';
import HouseCuspsSection from './components/HouseCuspsSection';
import MidpointsSection from './components/MidpointsSection';
import AspectsSection from './components/AspectsSection';
import UranianPointsSection from './components/UranianPointsSection';
import DignitiesSection from './components/DignitiesSection';
import ExportControls from './components/ExportControls';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import AIInterpretationSection from './components/AIInterpretationSection';
import { generateChartInterpretation } from '../../services/aiInterpretationService';

const AstrologicalResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('planetary-positions');
  const [calculationStatus, setCalculationStatus] = useState('idle');
  const [sectionVisibility, setSectionVisibility] = useState({
    'ai-interpretation': true,
    'planetary-positions': true,
    'house-cusps': true,
    'midpoints': true,
    'aspects': true,
    'uranian-points': true,
    'dignities': true
  });

  const [aiInterpretation, setAiInterpretation] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  const astrologicalData = location?.state?.astrologicalData || null;

  const mockAstrologicalData = {
    planetaryPositions: [
      { nameTh: "ดวงอาทิตย์", nameEn: "Sun", degree: "125.45", zodiacTh: "สิงห์", zodiacEn: "Leo", house: "5" },
      { nameTh: "ดวงจันทร์", nameEn: "Moon", degree: "78.23", zodiacTh: "เมถุน", zodiacEn: "Gemini", house: "3" },
      { nameTh: "พุธ", nameEn: "Mercury", degree: "142.67", zodiacTh: "สิงห์", zodiacEn: "Leo", house: "6" },
      { nameTh: "ดาวศุกร์", nameEn: "Venus", degree: "98.12", zodiacTh: "กรกฎ", zodiacEn: "Cancer", house: "4" },
      { nameTh: "ดาวอังคาร", nameEn: "Mars", degree: "215.89", zodiacTh: "พิจิก", zodiacEn: "Scorpio", house: "8" },
      { nameTh: "ดาวพฤหัสบดี", nameEn: "Jupiter", degree: "45.34", zodiacTh: "พฤษภ", zodiacEn: "Taurus", house: "2" },
      { nameTh: "ดาวเสาร์", nameEn: "Saturn", degree: "312.56", zodiacTh: "มังกร", zodiacEn: "Capricorn", house: "11" },
      { nameTh: "ดาวยูเรนัส", nameEn: "Uranus", degree: "178.90", zodiacTh: "กันย์", zodiacEn: "Virgo", house: "7" },
      { nameTh: "ดาวเนปจูน", nameEn: "Neptune", degree: "267.45", zodiacTh: "ธนู", zodiacEn: "Sagittarius", house: "9" },
      { nameTh: "ดาวพลูโต", nameEn: "Pluto", degree: "334.78", zodiacTh: "มังกร", zodiacEn: "Capricorn", house: "12" }
    ],
    houseCusps: [
      { houseNumber: "1", cuspDegree: "15.23", zodiacTh: "เมษ", zodiacEn: "Aries" },
      { houseNumber: "2", cuspDegree: "42.67", zodiacTh: "พฤษภ", zodiacEn: "Taurus" },
      { houseNumber: "3", cuspDegree: "68.45", zodiacTh: "เมถุน", zodiacEn: "Gemini" },
      { houseNumber: "4", cuspDegree: "95.12", zodiacTh: "กรกฎ", zodiacEn: "Cancer" },
      { houseNumber: "5", cuspDegree: "122.89", zodiacTh: "สิงห์", zodiacEn: "Leo" },
      { houseNumber: "6", cuspDegree: "148.34", zodiacTh: "กันย์", zodiacEn: "Virgo" },
      { houseNumber: "7", cuspDegree: "195.23", zodiacTh: "ตุลย์", zodiacEn: "Libra" },
      { houseNumber: "8", cuspDegree: "222.67", zodiacTh: "พิจิก", zodiacEn: "Scorpio" },
      { houseNumber: "9", cuspDegree: "248.45", zodiacTh: "ธนู", zodiacEn: "Sagittarius" },
      { houseNumber: "10", cuspDegree: "275.12", zodiacTh: "มังกร", zodiacEn: "Capricorn" },
      { houseNumber: "11", cuspDegree: "302.89", zodiacTh: "กุมภ์", zodiacEn: "Aquarius" },
      { houseNumber: "12", cuspDegree: "328.34", zodiacTh: "มีน", zodiacEn: "Pisces" }
    ],
    midpoints: [
      { planetA: "Sun", planetB: "Moon", midpointDegree: "101.84", orbDeviation: "2.34" },
      { planetA: "Sun", planetB: "Mercury", midpointDegree: "134.06", orbDeviation: "1.12" },
      { planetA: "Sun", planetB: "Venus", midpointDegree: "111.79", orbDeviation: "3.45" },
      { planetA: "Moon", planetB: "Mercury", midpointDegree: "110.45", orbDeviation: "0.89" },
      { planetA: "Moon", planetB: "Venus", midpointDegree: "88.18", orbDeviation: "2.67" },
      { planetA: "Mercury", planetB: "Venus", midpointDegree: "120.40", orbDeviation: "1.56" },
      { planetA: "Mars", planetB: "Jupiter", midpointDegree: "130.62", orbDeviation: "4.23" },
      { planetA: "Saturn", planetB: "Uranus", midpointDegree: "245.73", orbDeviation: "2.90" },
      { planetA: "Neptune", planetB: "Pluto", midpointDegree: "301.12", orbDeviation: "1.78" }
    ],
    aspects: [
      { planetA: "Sun", aspectTypeTh: "สหัสสะ", aspectTypeEn: "Conjunction", planetB: "Mercury", orb: "1.22", isMajor: true },
      { planetA: "Sun", aspectTypeTh: "สามเหลี่ยม", aspectTypeEn: "Trine", planetB: "Jupiter", orb: "2.11", isMajor: true },
      { planetA: "Moon", aspectTypeTh: "สี่เหลี่ยม", aspectTypeEn: "Square", planetB: "Mars", orb: "3.34", isMajor: true },
      { planetA: "Moon", aspectTypeTh: "หกเหลี่ยม", aspectTypeEn: "Sextile", planetB: "Venus", orb: "1.89", isMajor: true },
      { planetA: "Mercury", aspectTypeTh: "ตรงข้าม", aspectTypeEn: "Opposition", planetB: "Uranus", orb: "2.77", isMajor: true },
      { planetA: "Venus", aspectTypeTh: "ควินคังซ์", aspectTypeEn: "Quincunx", planetB: "Saturn", orb: "1.45", isMajor: false },
      { planetA: "Mars", aspectTypeTh: "เซมิสแควร์", aspectTypeEn: "Semi-square", planetB: "Neptune", orb: "0.98", isMajor: false },
      { planetA: "Jupiter", aspectTypeTh: "เซสควิควอดเรต", aspectTypeEn: "Sesquiquadrate", planetB: "Pluto", orb: "2.34", isMajor: false }
    ],
    uranianPoints: {
      fatePoints: [
        { nameTh: "จุดขึ้น (ASC)", nameEn: "Ascendant", degree: "15.23", zodiacTh: "เมษ", zodiacEn: "Aries" },
        { nameTh: "จุดสูงสุด (MC)", nameEn: "Midheaven", degree: "275.12", zodiacTh: "มังกร", zodiacEn: "Capricorn" },
        { nameTh: "ดวงอาทิตย์", nameEn: "Sun", degree: "125.45", zodiacTh: "สิงห์", zodiacEn: "Leo" },
        { nameTh: "ดวงจันทร์", nameEn: "Moon", degree: "78.23", zodiacTh: "เมถุน", zodiacEn: "Gemini" },
        { nameTh: "โหนดเหนือ", nameEn: "North Node", degree: "189.67", zodiacTh: "ตุลย์", zodiacEn: "Libra" },
        { nameTh: "จุดเมษ", nameEn: "Aries Point", degree: "0.00", zodiacTh: "เมษ", zodiacEn: "Aries" }
      ],
      hypotheticalPlanets: [
        { nameTh: "คิวปิโด", nameEn: "Cupido", degree: "56.78", zodiacTh: "พฤษภ", zodiacEn: "Taurus" },
        { nameTh: "เฮดีส", nameEn: "Hades", degree: "123.45", zodiacTh: "สิงห์", zodiacEn: "Leo" },
        { nameTh: "ซีอุส", nameEn: "Zeus", degree: "234.89", zodiacTh: "พิจิก", zodiacEn: "Scorpio" },
        { nameTh: "โครนอส", nameEn: "Kronos", degree: "298.12", zodiacTh: "กุมภ์", zodiacEn: "Aquarius" },
        { nameTh: "อพอลโล", nameEn: "Apollon", degree: "167.34", zodiacTh: "กันย์", zodiacEn: "Virgo" },
        { nameTh: "แอดมีทอส", nameEn: "Admetos", degree: "312.67", zodiacTh: "มังกร", zodiacEn: "Capricorn" },
        { nameTh: "วัลคานัส", nameEn: "Vulcanus", degree: "45.90", zodiacTh: "พฤษภ", zodiacEn: "Taurus" },
        { nameTh: "โพไซดอน", nameEn: "Poseidon", degree: "278.23", zodiacTh: "มังกร", zodiacEn: "Capricorn" }
      ]
    },
    dignities: [
      { planetTh: "ดวงอาทิตย์", planetEn: "Sun", dignityStatusTh: "ยกย่อง", dignityStatusEn: "Exaltation", houseRulershipTh: "ปกครองบ้านที่ 5", houseRulershipEn: "Rules House 5" },
      { planetTh: "ดวงจันทร์", planetEn: "Moon", dignityStatusTh: "ที่พำนัก", dignityStatusEn: "Domicile", houseRulershipTh: "ปกครองบ้านที่ 4", houseRulershipEn: "Rules House 4" },
      { planetTh: "พุธ", planetEn: "Mercury", dignityStatusTh: "ที่พำนัก", dignityStatusEn: "Domicile", houseRulershipTh: "ปกครองบ้านที่ 3, 6", houseRulershipEn: "Rules Houses 3, 6" },
      { planetTh: "ดาวศุกร์", planetEn: "Venus", dignityStatusTh: "เสื่อมศักดิ์", dignityStatusEn: "Detriment", houseRulershipTh: "ปกครองบ้านที่ 2, 7", houseRulershipEn: "Rules Houses 2, 7" },
      { planetTh: "ดาวอังคาร", planetEn: "Mars", dignityStatusTh: "ที่พำนัก", dignityStatusEn: "Domicile", houseRulershipTh: "ปกครองบ้านที่ 1, 8", houseRulershipEn: "Rules Houses 1, 8" },
      { planetTh: "ดาวพฤหัสบดี", planetEn: "Jupiter", dignityStatusTh: "ยกย่อง", dignityStatusEn: "Exaltation", houseRulershipTh: "ปกครองบ้านที่ 9, 12", houseRulershipEn: "Rules Houses 9, 12" },
      { planetTh: "ดาวเสาร์", planetEn: "Saturn", dignityStatusTh: "ตกต่ำ", dignityStatusEn: "Fall", houseRulershipTh: "ปกครองบ้านที่ 10, 11", houseRulershipEn: "Rules Houses 10, 11" }
    ]
  };

  const displayData = astrologicalData || mockAstrologicalData;

  useEffect(() => {
    if (!location?.state?.astrologicalData) {
      console.warn('No astrological data received from API, using mock data');
    }
    
    setCalculationStatus('loading');
    const timer = setTimeout(() => {
      setCalculationStatus('success');
      setTimeout(() => setCalculationStatus('idle'), 3000);
    }, 2000);

    return () => clearTimeout(timer);
  }, [location?.state]);

  const toggleSection = (sectionId) => {
    setSectionVisibility(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    alert('การส่งออก PDF จะถูกดำเนินการในเวอร์ชันเต็ม\nPDF export will be implemented in full version');
  };

  const handleReturnToInput = () => {
    navigate('/birth-data-input');
  };

  const handleGenerateAIInterpretation = async () => {
    setAiLoading(true);
    setAiError(null);
    
    try {
      const chartData = astrologicalData || mockAstrologicalData;
      const result = await generateChartInterpretation(chartData);
      
      if (result?.success) {
        setAiInterpretation(result?.interpretation);
      } else {
        setAiError('ไม่สามารถสร้างการวิเคราะห์ได้ กรุณาลองอีกครั้ง');
      }
    } catch (error) {
      setAiError(error?.message || 'เกิดข้อผิดพลาดในการสร้างการวิเคราะห์');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>ผลการวิเคราะห์แผนภูมิดาวเคราะห์ | ThaiAstroChart</title>
        <meta name="description" content="ดูผลการวิเคราะห์แผนภูมิดาวเคราะห์แบบละเอียด รวมถึงตำแหน่งดาวเคราะห์ จุดเริ่มต้นบ้าน จุดกึ่งกลาง มุมระหว่างดาว และศักดิ์ศรีดาว" />
      </Helmet>
      <div className="min-h-screen bg-gradient-cosmic relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-2 h-2 bg-cosmic-gold rounded-full animate-twinkle" />
          <div className="absolute top-40 right-20 w-1 h-1 bg-cosmic-lightGold rounded-full animate-twinkle" style={{ animationDelay: '1s' }} />
          <div className="absolute top-60 left-1/4 w-1.5 h-1.5 bg-cosmic-gold rounded-full animate-twinkle" style={{ animationDelay: '2s' }} />
          <div className="absolute bottom-40 right-1/3 w-2 h-2 bg-cosmic-lightGold rounded-full animate-twinkle" style={{ animationDelay: '0.5s' }} />
        </div>
        
        <Header />
        
        <CalculationStatus 
          status={calculationStatus}
          onRetry={() => setCalculationStatus('loading')}
          error={null}
        />

        <main className="pt-24 pb-16 relative z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header Section */}
            <div className="mb-10">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="text-center lg:text-left">
                  <div className="flex items-center justify-center lg:justify-start mb-4">
                    <div className="w-14 h-14 bg-gradient-gold rounded-xl flex items-center justify-center shadow-gold mr-4">
                      <Icon name="FileText" size={28} color="#0a1128" />
                    </div>
                    <div>
                      <h1 className="text-3xl md:text-4xl font-heading font-bold text-cosmic-gold">
                        ผลการวิเคราะห์แผนภูมิดาวเคราะห์
                      </h1>
                      <p className="text-sm md:text-base text-cosmic-lightGold/70 mt-1">
                        Astrological Chart Analysis Results
                      </p>
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={handleReturnToInput}
                  className="w-full lg:w-auto border-cosmic-gold/30 text-cosmic-gold hover:bg-cosmic-gold/10 font-thai"
                >
                  กลับไปแก้ไขข้อมูล
                </Button>
              </div>
            </div>

            {/* Birth Chart Display Placeholder */}
            <div className="mb-10">
              <div className="bg-cosmic-midnight/80 backdrop-blur-xl border-2 border-cosmic-gold/30 rounded-3xl p-8 md:p-12">
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-cosmic-gold mb-3">
                    แผนภูมิดาวเคราะห์
                  </h2>
                  <p className="text-cosmic-lightGold/70 font-thai">
                    Birth Chart Wheel Visualization
                  </p>
                </div>
                
                <div className="flex items-center justify-center">
                  <div className="relative w-full max-w-md aspect-square">
                    <div className="absolute inset-0 bg-gradient-gold rounded-full opacity-10 blur-3xl" />
                    <div className="relative w-full h-full border-4 border-cosmic-gold/40 rounded-full flex items-center justify-center">
                      <div className="w-3/4 h-3/4 border-2 border-cosmic-gold/30 rounded-full flex items-center justify-center">
                        <div className="w-1/2 h-1/2 border border-cosmic-gold/20 rounded-full flex items-center justify-center">
                          <Icon name="Star" size={48} className="text-cosmic-gold" />
                        </div>
                      </div>
                    </div>
                    {/* Zodiac markers */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-cosmic-gold rounded-full" />
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-3 h-3 bg-cosmic-gold rounded-full" />
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 bg-cosmic-gold rounded-full" />
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 bg-cosmic-gold rounded-full" />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Navigation Tabs */}
              <div className="lg:col-span-3">
                <div className="sticky top-24">
                  <div className="bg-cosmic-midnight/80 backdrop-blur-xl border border-cosmic-gold/30 rounded-2xl p-4">
                    <h3 className="text-lg font-semibold text-cosmic-gold mb-4 font-thai px-2">
                      หมวดหมู่ข้อมูล
                    </h3>
                    <ResultsSectionNav 
                      activeSection={activeSection}
                      onSectionChange={setActiveSection}
                    />
                  </div>
                </div>
              </div>

              {/* Content Sections */}
              <div className="lg:col-span-9 space-y-6">
                <AIInterpretationSection
                  interpretation={aiInterpretation}
                  isLoading={aiLoading}
                  error={aiError}
                  onGenerate={handleGenerateAIInterpretation}
                  isVisible={sectionVisibility?.['ai-interpretation']}
                  onToggle={() => toggleSection('ai-interpretation')}
                />

                <ExportControls 
                  onPrint={handlePrint}
                  onExport={handleExport}
                />

                <PlanetaryPositionsSection
                  data={displayData?.planetaryPositions}
                  isVisible={sectionVisibility?.['planetary-positions']}
                  onToggle={() => toggleSection('planetary-positions')}
                />

                <HouseCuspsSection
                  data={displayData?.houseCusps}
                  isVisible={sectionVisibility?.['house-cusps']}
                  onToggle={() => toggleSection('house-cusps')}
                />

                <MidpointsSection
                  data={displayData?.midpoints}
                  isVisible={sectionVisibility?.['midpoints']}
                  onToggle={() => toggleSection('midpoints')}
                />

                <AspectsSection
                  data={displayData?.aspects}
                  isVisible={sectionVisibility?.['aspects']}
                  onToggle={() => toggleSection('aspects')}
                />

                <UranianPointsSection
                  data={displayData?.uranianPoints}
                  isVisible={sectionVisibility?.['uranian-points']}
                  onToggle={() => toggleSection('uranian-points')}
                />

                <DignitiesSection
                  data={displayData?.dignities}
                  isVisible={sectionVisibility?.['dignities']}
                  onToggle={() => toggleSection('dignities')}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AstrologicalResults;